import { useEffect, useMemo, useState } from "react";
import { Loader2, TrendingUp, ChevronDown, ChevronUp, DollarSign, FileDown, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Quarter-over-quarter savings history for a single client. Reads the same
 * data SavingsComposer writes to (client_savings collection) and groups it
 * into calendar quarters so founders can eyeball momentum at a glance.
 *
 * Design goals:
 *   • Zero interaction required — expands one section at a time
 *   • Sparkline bar chart across the last 8 quarters
 *   • Per-quarter breakdown (individual entries + category rollups)
 */
export default function SavingsTimeline({ clientId, refreshKey = 0 }) {
  const [state, setState] = useState({ loading: true, entries: [], total: 0, per: {} });
  const [expanded, setExpanded] = useState(null);
  const [downloadingQ, setDownloadingQ] = useState(null);
  const [emailingQ, setEmailingQ] = useState(null);

  const emailQuarterly = async (qkey) => {
    setEmailingQ(qkey || "latest");
    try {
      const suffix = qkey ? `?quarter=${encodeURIComponent(qkey)}` : "";
      const { data } = await api.post(
        `/admin/vault/${encodeURIComponent(clientId)}/quarterly-report/email${suffix}`,
      );
      toast.success(`Report emailed to ${data.to}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not email the report.");
    } finally {
      setEmailingQ(null);
    }
  };

  const downloadQuarterly = async (qkey) => {
    setDownloadingQ(qkey || "latest");
    try {
      const suffix = qkey ? `?quarter=${encodeURIComponent(qkey)}` : "";
      const res = await api.get(
        `/admin/vault/${encodeURIComponent(clientId)}/quarterly-report.pdf${suffix}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Elevare_Quarterly_${qkey || "latest"}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
      toast.success("Quarterly report downloaded.");
    } catch {
      toast.error("Could not build the quarterly report.");
    } finally {
      setDownloadingQ(null);
    }
  };

  useEffect(() => {
    if (!clientId) return;
    setState((s) => ({ ...s, loading: true }));
    api.get(`/admin/vault/${encodeURIComponent(clientId)}/savings`)
      .then(({ data }) => setState({
        loading: false,
        entries: data.entries || [],
        total: data.total_cents || 0,
        per: data.per_founder_cents || {},
      }))
      .catch(() => setState({ loading: false, entries: [], total: 0, per: {} }));
  }, [clientId, refreshKey]);

  // ---- Bucket entries into quarters (YYYY-Qn) ----
  const quarters = useMemo(() => {
    const buckets = new Map();
    for (const e of state.entries) {
      const d = new Date(e.created_at || Date.now());
      if (isNaN(d.getTime())) continue;
      const q = Math.floor(d.getMonth() / 3) + 1;
      const key = `${d.getFullYear()}-Q${q}`;
      if (!buckets.has(key)) {
        buckets.set(key, { key, year: d.getFullYear(), q, total: 0, entries: [], categories: {} });
      }
      const bucket = buckets.get(key);
      const amt = Number(e.amount_cents || 0);
      bucket.total += amt;
      bucket.entries.push(e);
      const cat = (e.category || "monthly_savings");
      bucket.categories[cat] = (bucket.categories[cat] || 0) + amt;
    }
    return Array.from(buckets.values()).sort((a, b) =>
      a.year !== b.year ? b.year - a.year : b.q - a.q,
    );
  }, [state.entries]);

  // Sparkline: last 8 quarters, oldest → newest
  const sparkline = useMemo(() => {
    const trailing = [...quarters].slice(0, 8).reverse();
    if (trailing.length === 0) return [];
    const max = Math.max(...trailing.map((q) => q.total), 1);
    return trailing.map((q) => ({ ...q, pct: (q.total / max) * 100 }));
  }, [quarters]);

  if (state.loading) {
    return (
      <div className="mt-4 rounded-xl border border-[#1e3d3a]/12 bg-white p-4 flex items-center justify-center" data-testid="savings-timeline-loading">
        <Loader2 className="h-4 w-4 animate-spin text-[#c9a961]" />
      </div>
    );
  }
  if (state.entries.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-[#1e3d3a]/12 bg-white p-4 text-center" data-testid="savings-timeline-empty">
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Savings history</p>
        <p className="text-[13px] text-[#1e3d3a]/55 mt-1">No savings logged for this client yet.</p>
      </div>
    );
  }

  return (
    <div data-testid="savings-timeline" className="mt-4 rounded-xl border border-[#c9a961]/40 bg-white p-4">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3" /> Savings timeline
          </p>
          <p className="text-2xl font-head text-[#1e3d3a] tabular-nums mt-0.5">
            {money(state.total)}
          </p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">
            {state.entries.length} entr{state.entries.length === 1 ? "y" : "ies"} · lifetime savings
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">
            {Object.entries(state.per).map(([who, cents]) => (
              <div key={who} data-testid={`savings-per-${who}`}>
                <span className="text-[#a3823f]">{who}</span> · <span className="tabular-nums text-[#1e3d3a]/80">{money(cents)}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            data-testid="quarterly-report-download"
            onClick={() => downloadQuarterly(null)}
            disabled={!!downloadingQ}
            title="Download this quarter's one-page success report"
            className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-[#c9a961]/50 bg-[#c9a961]/8 text-[#a3823f] px-2.5 py-1 text-[9px] uppercase tracking-[0.18em] hover:bg-[#c9a961]/16 transition-colors duration-300 disabled:opacity-60"
          >
            {downloadingQ === "latest" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
            Quarterly report
          </button>
          <button
            type="button"
            data-testid="quarterly-report-email"
            onClick={() => emailQuarterly(null)}
            disabled={!!emailingQ}
            title="Email this quarter's report to the client"
            className="mt-1.5 ml-1.5 inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-2.5 py-1 text-[9px] uppercase tracking-[0.18em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
          >
            {emailingQ === "latest" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Send to client
          </button>
        </div>
      </div>

      {/* Sparkline — trailing 8 quarters */}
      {sparkline.length > 0 && (
        <div data-testid="savings-sparkline" className="flex items-end justify-between gap-1 h-16 pt-2 border-t border-[#1e3d3a]/6">
          {sparkline.map((q) => (
            <div key={q.key} className="flex-1 flex flex-col items-center justify-end min-w-0" title={`${q.key}: ${money(q.total)}`}>
              <div
                className="w-full max-w-[24px] rounded-t bg-gradient-to-t from-[#c9a961] to-[#a3823f] transition-all duration-500"
                style={{ height: `${Math.max(3, q.pct)}%` }}
              />
              <span className="text-[8px] uppercase tracking-[0.14em] text-[#1e3d3a]/40 mt-1 truncate">
                {q.key.split("-")[1]}·{String(q.year).slice(-2)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Expandable per-quarter breakdown */}
      <ul className="mt-3 divide-y divide-[#1e3d3a]/6" data-testid="savings-quarters">
        {quarters.map((q) => {
          const open = expanded === q.key;
          const catList = Object.entries(q.categories).sort((a, b) => b[1] - a[1]);
          return (
            <li key={q.key} data-testid={`savings-quarter-${q.key}`} className="py-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExpanded(open ? null : q.key)}
                  className="flex-1 flex items-center justify-between text-left group"
                  data-testid={`savings-quarter-toggle-${q.key}`}
                >
                  <div className="flex items-center gap-2">
                    {open ? <ChevronUp className="h-3 w-3 text-[#1e3d3a]/50" /> : <ChevronDown className="h-3 w-3 text-[#1e3d3a]/50" />}
                    <span className="text-[13px] font-medium text-[#1e3d3a] group-hover:text-[#a3823f] transition-colors duration-200">
                      {q.key.replace("-Q", " · Q")}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">
                      {q.entries.length} entr{q.entries.length === 1 ? "y" : "ies"}
                    </span>
                  </div>
                  <span className="text-[13px] tabular-nums font-medium text-[#a3823f]">
                    {money(q.total)}
                  </span>
                </button>
                <button
                  type="button"
                  data-testid={`quarterly-report-${q.key}`}
                  onClick={() => downloadQuarterly(q.key)}
                  disabled={!!downloadingQ}
                  title={`Download the ${q.key} success report`}
                  className="shrink-0 h-6 w-6 rounded-full border border-[#1e3d3a]/12 grid place-items-center text-[#1e3d3a]/40 hover:text-[#a3823f] hover:border-[#c9a961] transition-colors duration-300 disabled:opacity-50"
                >
                  {downloadingQ === q.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
                </button>
                <button
                  type="button"
                  data-testid={`quarterly-report-email-${q.key}`}
                  onClick={() => emailQuarterly(q.key)}
                  disabled={!!emailingQ}
                  title={`Email the ${q.key} report to the client`}
                  className="shrink-0 h-6 w-6 rounded-full border border-[#1e3d3a]/12 grid place-items-center text-[#1e3d3a]/40 hover:text-white hover:bg-[#1e3d3a] hover:border-[#1e3d3a] transition-colors duration-300 disabled:opacity-50"
                >
                  {emailingQ === q.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                </button>
              </div>

              {open && (
                <div className="mt-2 ml-4 space-y-2" data-testid={`savings-quarter-expand-${q.key}`}>
                  {catList.length > 1 && (
                    <div className="flex flex-wrap gap-1.5">
                      {catList.map(([cat, cents]) => (
                        <span key={cat} className="inline-flex items-center gap-1 rounded-full bg-[#c9a961]/12 text-[#a3823f] px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]">
                          {cat.replace(/_/g, " ")}
                          <span className="tabular-nums text-[#1e3d3a]/70">· {money(cents)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <ul className="space-y-1.5">
                    {q.entries.map((e) => (
                      <li key={e.id} className="flex items-baseline justify-between gap-3 text-[11px] border-l-2 border-[#c9a961]/30 pl-2" data-testid={`savings-entry-${e.id}`}>
                        <div className="min-w-0">
                          <div className="text-[#1e3d3a] truncate">
                            <DollarSign className="h-3 w-3 inline text-[#a3823f]" />
                            <span className="tabular-nums ml-0.5">{money(e.amount_cents)}</span>
                            {e.note && <span className="text-[#1e3d3a]/60"> · {e.note}</span>}
                          </div>
                          <div className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/40">
                            {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                            {e.credited_to && ` · ${e.credited_to}`}
                            {e.category && ` · ${e.category.replace(/_/g, " ")}`}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const money = (cents) =>
  (Number(cents || 0) / 100).toLocaleString(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  });
